import { useId } from "react";
import Card from "components/card";
import Skeleton from "components/skeleton/Skeleton";

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * SettingsSection — the one card shell used by every Settings tab.
 * Stacked layout: heading + description (and an optional action on the right),
 * then the section body at full width so tables and lists are never cramped.
 *
 * @param {object} props
 * @param {React.ReactNode} props.title Section heading (h2).
 * @param {React.ReactNode} [props.description] Muted helper text under the heading.
 * @param {React.ReactNode} [props.action] Buttons rendered right-aligned in the header.
 * @param {string} [props.className]
 * @param {React.ReactNode} [props.children] Section body.
 */
export default function SettingsSection({ title, description, action, className, children }) {
  const headingId = useId();

  return (
    <Card as="section" padding="md" aria-labelledby={headingId} className={className}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 flex-1">
          <h2 id={headingId} className="text-heading">
            {title}
          </h2>
          {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
        </div>
        {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
      </div>
      {children && <div className="mt-6">{children}</div>}
    </Card>
  );
}

/**
 * SettingsSubsection — a titled group inside a SettingsSection body. Groups after
 * the first are separated by a divider (never a nested card).
 *
 * @param {object} props
 * @param {React.ReactNode} props.title Sub-heading (h3).
 * @param {React.ReactNode} [props.description]
 * @param {React.ReactNode} [props.action] Right-aligned controls.
 * @param {string} [props.className]
 * @param {React.ReactNode} [props.children]
 */
export function SettingsSubsection({ title, description, action, className, children }) {
  return (
    <div className={cx("border-line [&:not(:first-child)]:mt-6 [&:not(:first-child)]:border-t [&:not(:first-child)]:pt-6", className)}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-fg">{title}</h3>
          {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
        </div>
        {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

/**
 * Footer row for per-section forms: right-aligned actions (Cancel, then the
 * primary submit) separated from the fields by a divider.
 */
export function SettingsFormActions({ children }) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-5">
      {children}
    </div>
  );
}

/**
 * Loading placeholder shaped like a SettingsSection (heading, description, body lines).
 *
 * @param {object} props
 * @param {number} [props.lines=4] Body placeholder lines.
 */
export function SettingsSectionSkeleton({ lines = 4 }) {
  return (
    <Card padding="md" aria-busy="true">
      <span className="sr-only" role="status">
        Loading
      </span>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="mt-2 h-4 w-72 max-w-full" />
      <Skeleton lines={lines} className="mt-6" />
    </Card>
  );
}
